// TrustChip — always-visible privacy / encryption / hosting reassurance
// in the top bar (Session 8 / B5). Resolves C8 in the plan.
//
// Click → opens a compact Radix dialog with the full reassurance text
// + a "Manage your data" link to /data-control. Hidden on <sm by design
// (per Session 8 design call): the mobile top bar is already packed
// with hamburger / currency / notifications / SOS.

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Shield, ShieldCheck, Lock, MapPin, FileCheck2, ArrowRight, X } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogPortal,
  DialogOverlay,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';

export default function TrustChip() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Your privacy and data"
        title="Your privacy and data"
        className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-colors"
        style={{
          background: 'var(--success-light)',
          color: 'var(--success)',
          border: '1px solid color-mix(in srgb, var(--success) 25%, transparent)',
        }}
        data-testid="trust-chip"
      >
        <Shield size={12} />
        India · DPDP
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogPortal>
          <DialogOverlay className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm" />
          <DialogContent
            className="fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2 w-[calc(100%-1.5rem)] max-w-md max-h-[90vh] overflow-y-auto p-0 nsofar-card"
            data-testid="trust-dialog"
          >
            <AnimatePresence>
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.18, ease: 'easeOut' }}
                className="p-5 sm:p-6"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ background: 'var(--success-light)' }}
                    >
                      <ShieldCheck size={20} style={{ color: 'var(--success)' }} />
                    </div>
                    <div>
                      <DialogTitle className="text-base font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        Your data, your control
                      </DialogTitle>
                      <DialogDescription className="text-xs" style={{ color: 'var(--text-muted)' }}>
                        How nsofar handles your family's information.
                      </DialogDescription>
                    </div>
                  </div>
                  <button onClick={() => setOpen(false)} className="p-1 rounded-lg hover:bg-[var(--hover-surface)]" aria-label="Close">
                    <X size={18} />
                  </button>
                </div>

                <ul className="space-y-3 mb-5">
                  <Bullet icon={MapPin} title="Hosted in India">
                    Your data lives on servers in the ap-south-1 region. It never leaves India unless you ask us to share it (e.g. to a partner you've consented to).
                  </Bullet>
                  <Bullet icon={Lock} title="Encrypted at rest and in transit">
                    Health information is encrypted with rotating per-tenant keys. We use TLS 1.2+ for every request between your device and our servers.
                  </Bullet>
                  <Bullet icon={FileCheck2} title="DPDP-aligned consent">
                    We track every consent decision with timestamps and version pins. You can withdraw any optional permission at any time without losing core care.
                  </Bullet>
                </ul>

                <button
                  type="button"
                  onClick={() => { setOpen(false); navigate('/data-control'); }}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-white"
                  style={{ background: 'var(--brand-primary)' }}
                  data-testid="trust-dialog-manage"
                >
                  Manage your data <ArrowRight size={14} />
                </button>
              </motion.div>
            </AnimatePresence>
          </DialogContent>
        </DialogPortal>
      </Dialog>
    </>
  );
}

function Bullet({ icon: Icon, title, children }) {
  return (
    <li className="flex items-start gap-3">
      <div
        className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
        style={{ background: 'var(--bg-main)' }}
      >
        <Icon size={14} style={{ color: 'var(--brand-primary)' }} />
      </div>
      <div>
        <p className="text-sm font-semibold" style={{ color: 'var(--text-main)' }}>{title}</p>
        <p className="text-xs leading-relaxed" style={{ color: 'var(--text-muted)' }}>{children}</p>
      </div>
    </li>
  );
}
